import React from "react";
import { api, ASSET_URL } from "../lib/api";
import { useSite } from "../lib/site-context";
import { useScrollReveal } from "../lib/motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export default function PreislistePage() {
  const { lang, t } = useSite();
  const [items, setItems] = React.useState([]);
  const [settings, setSettings] = React.useState(null);
  const [search, setSearch] = React.useState("");
  const [mediaMap, setMediaMap] = React.useState({});
  useScrollReveal();

  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/pricing`).then(r => r.json()).then(setItems);
    api.pricingSettings().then(setSettings).catch(() => {});
    api.media("image").then(l => setMediaMap(Object.fromEntries(l.map(m => [m.id, m])))).catch(() => {});
    document.title = "Preisliste · Dr. B.-Digitec";
  }, []);

  const S = settings || {};
  const txt = (key) => lang === "en" ? (S[`${key}_en`] || S[`${key}_de`] || "") : (S[`${key}_de`] || "");

  const filtered = items.filter(it => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (it.name_de || "").toLowerCase().includes(q) || (it.description_de || "").toLowerCase().includes(q) || (it.category || "").toLowerCase().includes(q);
  });
  const byCat = React.useMemo(() => {
    const m = {};
    filtered.forEach(it => { (m[it.category] = m[it.category] || []).push(it); });
    return m;
  }, [filtered]);

  return (
    <main className="min-h-screen pt-36 md:pt-44 pb-20 bg-[var(--bg)]" data-testid="preisliste-page">
      <div className="max-w-[1300px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-12">
          <div className="col-span-12 md:col-span-6">
            <div className="brut-tag mb-6">{txt("eyebrow") || t("Preisliste", "Pricing")}</div>
            <h1 className="brut-h1">{txt("title") || t("Transparente Laborpreise.", "Transparent lab prices.")}</h1>
          </div>
          <div className="col-span-12 md:col-span-6 md:pl-12">
            <p className="brut-body max-w-xl whitespace-pre-line">
              {txt("intro")}
            </p>
            <Link to={S.cta_url || "/praxis-check"} className="brut-btn mt-6">{txt("cta_label") || t("Individuellen KVA anfragen", "Request individual quote")} <ArrowUpRight size={16} /></Link>
          </div>
        </div>

        <div className="mb-10 max-w-md mx-auto md:mx-0">
          <input
            type="search"
            placeholder={t("Versorgung, Material, Stichwort …", "Search by name, material …")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="brut-input"
            data-testid="pricing-search"
          />
        </div>

        <div className="space-y-12">
          {Object.entries(byCat).map(([cat, list], catIdx) => (
            <section key={cat} data-reveal>
              <div className="flex items-baseline justify-between mb-6 border-b border-[var(--line)] pb-4">
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight">{cat}</h2>
                <span className="text-sm font-bold opacity-60">{list.length} {t("Leistungen", "items")}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {list.map((it, i) => (
                  <div
                    key={it.id}
                    className="p-6 rounded-3xl bg-[var(--bg)] lift-on-hover"
                    style={{ boxShadow: "var(--neu-raised-sm)" }}
                    data-reveal data-reveal-delay={String((i % 3) + 1)}
                    data-testid={`pricing-item-${it.id}`}
                  >
                    {it.image_media_id && mediaMap[it.image_media_id] && (
                      <div className="mb-4 overflow-hidden rounded-2xl aspect-[16/9] bg-white" style={{ boxShadow: "var(--neu-inset-sm)" }}>
                        <img
                          src={ASSET_URL(mediaMap[it.image_media_id].variants?.webp || mediaMap[it.image_media_id].variants?.thumb || mediaMap[it.image_media_id].variants?.original)}
                          alt={lang === "en" ? (it.name_en || it.name_de) : it.name_de}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          data-testid={`pricing-item-image-${it.id}`}
                        />
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="text-lg md:text-xl font-bold tracking-tight leading-tight">{lang === "en" ? (it.name_en || it.name_de) : it.name_de}</div>
                      <div className="text-right shrink-0">
                        {it.price_label ? (
                          <div className="text-sm font-bold opacity-70 whitespace-nowrap">{it.price_label}</div>
                        ) : (
                          <>
                            <div className="text-2xl md:text-3xl font-extrabold tracking-tight whitespace-nowrap">{it.price_from.toFixed(0)} €</div>
                            <div className="text-[11px] font-bold opacity-60 uppercase tracking-wider">{t("ab", "from")}</div>
                          </>
                        )}
                      </div>
                    </div>
                    {it.description_de && (
                      <p className="text-sm leading-relaxed opacity-75">{lang === "en" ? (it.description_en || it.description_de) : it.description_de}</p>
                    )}
                    <div className="mt-3 text-xs font-bold opacity-50 uppercase tracking-wider">{lang === "en" ? (it.unit_en || it.unit_de) : it.unit_de}</div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 p-8 rounded-3xl text-center" style={{ boxShadow: "var(--neu-inset)" }}>
          <p className="brut-body max-w-2xl mx-auto whitespace-pre-line">
            {txt("note")}
          </p>
        </div>
      </div>
    </main>
  );
}
